import React, { useState, useEffect, useRef } from 'react';
import { X, Search, Smartphone, Calendar, Bell, Star } from 'lucide-react';

interface Topic {
  id: string;
  label: string;
  icon: React.ReactNode;
  recommended?: boolean;
  content: React.ReactNode;
}

interface CustomerHelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CustomerHelpModal: React.FC<CustomerHelpModalProps> = ({ isOpen, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTopic, setActiveTopic] = useState('pwa');
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const mobileDropdownRef = useRef<HTMLDivElement>(null);

  const topics: Topic[] = [
    {
      id: 'pwa',
      label: 'Install as an app',
      icon: <Smartphone className="h-4 w-4" />,
      recommended: true,
      content: (
        <div>
          <div className="flex items-start gap-3 mb-4">
            <div className="w-9 h-9 rounded-[10px] flex items-center justify-center text-base flex-shrink-0 bg-blue-50">
              ⚡
            </div>
            <div>
              <div className="font-bold text-lg text-gray-900">Install AACore Booking for faster access</div>
              <div className="text-sm text-gray-600 mt-1">Install once so it opens like a regular app from your home screen or desktop.</div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="border border-gray-200 rounded-xl p-3 bg-white">
              <div className="flex gap-3 items-start mb-2">
                <div className="w-9 h-9 rounded-[10px] flex items-center justify-center text-base flex-shrink-0 bg-blue-50">
                  📱
                </div>
                <div>
                  <div className="font-semibold text-gray-900">iPhone (Safari)</div>
                  <div className="text-sm text-gray-600">Best experience using Safari.</div>
                </div>
              </div>

              <ol className="text-sm text-gray-700 space-y-1 mb-0">
                <li><strong>Open</strong> AACore Booking in <strong>Safari</strong>.</li>
                <li><strong>Tap</strong> the <strong>...</strong> in the bottom right.</li>
                <li><strong>Tap</strong> <strong>Share</strong>.</li>
                <li><strong>Scroll</strong> and select <strong>Add to Home Screen</strong>.</li>
                <li><strong>Optional</strong> edit the name if you choose.</li>
                <li><strong>Tap</strong> <strong>Add</strong>.</li>
              </ol>

              <div className="bg-gray-50 border border-gray-200 rounded-lg mt-3 p-2">
                <span className="text-sm text-gray-600">Leave <strong>Open as Web App</strong> set to <strong>On</strong>.</span>
              </div>
            </div>

            <div className="border border-gray-200 rounded-xl p-3 bg-white">
              <div className="flex gap-3 items-start mb-2">
                <div className="w-9 h-9 rounded-[10px] flex items-center justify-center text-base flex-shrink-0 bg-blue-50">
                  🤖
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Android</div>
                  <div className="text-sm text-gray-600">Chrome and Edge supported.</div>
                </div>
              </div>

              <div className="font-semibold text-sm text-gray-900 mt-2">Chrome</div>
              <ol className="text-sm text-gray-700 space-y-1 mb-3">
                <li><strong>Open</strong> AACore Booking in <strong>Chrome</strong>.</li>
                <li><strong>Tap</strong> the vertical <strong>⋮</strong> (three dots).</li>
                <li><strong>Select</strong> <strong>Add to Home Screen</strong>.</li>
              </ol>

              <div className="font-semibold text-sm text-gray-900">Edge</div>
              <ol className="text-sm text-gray-700 space-y-1 mb-0">
                <li><strong>Open</strong> AACore Booking in <strong>Edge</strong>.</li>
                <li><strong>Tap</strong> the menu icon.</li>
                <li><strong>Select</strong> <strong>Add to phone</strong>.</li>
              </ol>
            </div>

            <div className="border border-gray-200 rounded-xl p-3 bg-white lg:col-span-2">
              <div className="flex gap-3 items-start mb-2">
                <div className="w-9 h-9 rounded-[10px] flex items-center justify-center text-base flex-shrink-0 bg-blue-50">
                  💻
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Desktop (Windows and macOS)</div>
                  <div className="text-sm text-gray-600">Works on Chrome, Edge, and other Chromium based browsers.</div>
                </div>
              </div>

              <ol className="text-sm text-gray-700 space-y-1 mb-0">
                <li><strong>Open</strong> AACore Booking in your browser.</li>
                <li><strong>Look</strong> on the right side of the address bar for an install icon.</li>
                <li><strong>Click</strong> <strong>Install</strong> or <strong>Install App</strong>.</li>
                <li><strong>Confirm</strong> when prompted.</li>
              </ol>
            </div>

            <div className="border border-gray-200 rounded-xl p-3 bg-white lg:col-span-2">
              <div className="flex gap-3 items-start">
                <div className="w-9 h-9 rounded-[10px] flex items-center justify-center text-base flex-shrink-0 bg-blue-50">
                  ✨
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Why install?</div>
                  <ul className="text-sm text-gray-700 space-y-1 mb-0 mt-1">
                    <li><strong>Faster access</strong> open in one tap or click.</li>
                    <li><strong>Separate window</strong> less browser clutter.</li>
                    <li><strong>Convenience</strong> no need to type the website each time.</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'manage',
      label: 'Manage appointments',
      icon: <Calendar className="h-4 w-4" />,
      content: (
        <div>
          <div className="flex items-start gap-3 mb-4">
            <div className="w-9 h-9 rounded-[10px] flex items-center justify-center text-base flex-shrink-0 bg-blue-50">
              📅
            </div>
            <div>
              <div className="font-bold text-lg text-gray-900">Manage appointments</div>
              <div className="text-sm text-gray-600 mt-1">View upcoming visits, reschedule when available, or cancel if needed.</div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="border border-gray-200 rounded-xl p-3 bg-white">
              <div className="font-semibold text-gray-900 mb-2">From your dashboard</div>
              <ol className="text-sm text-gray-700 space-y-1 mb-0">
                <li>Open <strong>My Dashboard</strong>.</li>
                <li>Select an upcoming appointment.</li>
                <li>Use <strong>Reschedule</strong> or <strong>Cancel</strong> if available.</li>
              </ol>
              <div className="bg-gray-50 border border-gray-200 rounded-lg mt-3 p-2">
                <span className="text-sm text-gray-600">Some businesses may limit changes close to the appointment time.</span>
              </div>
            </div>
            <div className="border border-gray-200 rounded-xl p-3 bg-white">
              <div className="font-semibold text-gray-900 mb-2">Tips</div>
              <ul className="text-sm text-gray-700 space-y-1 mb-0">
                <li>Keep your email and phone up to date for confirmations.</li>
                <li>Check the business page for policies and hours.</li>
                <li>If you cannot reschedule online, contact the business directly.</li>
              </ul>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'notify',
      label: 'Notifications and reminders',
      icon: <Bell className="h-4 w-4" />,
      content: (
        <div>
          <div className="flex items-start gap-3 mb-4">
            <div className="w-9 h-9 rounded-[10px] flex items-center justify-center text-base flex-shrink-0 bg-blue-50">
              🔔
            </div>
            <div>
              <div className="font-bold text-lg text-gray-900">Notifications and reminders</div>
              <div className="text-sm text-gray-600 mt-1">Stay informed about confirmations, changes, and reminders.</div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="border border-gray-200 rounded-xl p-3 bg-white">
              <div className="font-semibold text-gray-900 mb-2">What you receive</div>
              <ul className="text-sm text-gray-700 space-y-1 mb-0">
                <li>Booking confirmations</li>
                <li>Schedule change notices</li>
                <li>Reminder messages before your appointment</li>
              </ul>
            </div>
            <div className="border border-gray-200 rounded-xl p-3 bg-white">
              <div className="font-semibold text-gray-900 mb-2">If you do not see emails</div>
              <ul className="text-sm text-gray-700 space-y-1 mb-0">
                <li>Check spam or promotions folders.</li>
                <li>Add <span className="font-mono text-xs">no-reply@aacore.net</span> to your safe sender list if needed.</li>
                <li>Confirm your email address in <strong>Profile Settings</strong>.</li>
              </ul>
            </div>
          </div>
        </div>
      )
    }
  ];

  const filteredTopics = topics.filter(topic =>
    topic.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (mobileDropdownRef.current && !mobileDropdownRef.current.contains(event.target as Node)) {
        setIsMobileDropdownOpen(false);
      }
    };

    if (isMobileDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileDropdownOpen]);

  useEffect(() => {
    if (isOpen && filteredTopics.length > 0 && !filteredTopics.find(t => t.id === activeTopic)) {
      setActiveTopic(filteredTopics[0].id);
    }
  }, [searchQuery, isOpen, filteredTopics, activeTopic]);

  const handleClose = () => {
    setSearchQuery('');
    setActiveTopic('pwa');
    setIsMobileDropdownOpen(false);
    onClose();
  };

  const handleTopicSelect = (topicId: string) => {
    setActiveTopic(topicId);
    setIsMobileDropdownOpen(false);
  };

  if (!isOpen) return null;

  const activeTopicData = topics.find(t => t.id === activeTopic);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="help-modal-title" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-6xl max-h-[75vh] md:max-h-[90vh] flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-start justify-between p-6 border-b border-gray-200 flex-shrink-0">
            <div>
              <h2 id="help-modal-title" className="text-xl font-bold text-gray-900">Help</h2>
              <p className="text-sm text-gray-600 mt-1">Select a topic or search.</p>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-lg hover:bg-gray-100"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            {/* Search */}
            <div className="p-6 pb-4 flex-shrink-0">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search help topics"
                  className="w-full pl-10 pr-4 py-2.5 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#0B4778] focus:border-transparent text-sm"
                />
              </div>
            </div>

            {/* Mobile Topic Selector - Above content on mobile */}
            <div className="md:hidden px-6 pb-4 flex-shrink-0" ref={mobileDropdownRef}>
              <label className="block text-sm font-semibold text-gray-900 mb-2">Topic</label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setIsMobileDropdownOpen(!isMobileDropdownOpen)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0B4778] focus:border-transparent bg-white text-left flex items-center justify-between"
                >
                  <div className="flex items-center gap-2">
                    {activeTopicData && (
                      <>
                        <span className="text-gray-500">{activeTopicData.icon}</span>
                        <span className="text-gray-900 flex items-center gap-1">
                          {activeTopicData.label}
                          {activeTopicData.recommended && <Star className="h-3 w-3 text-[#0B4778] fill-current" />}
                        </span>
                      </>
                    )}
                  </div>
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {isMobileDropdownOpen && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredTopics.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-gray-600">No topics found</div>
                    ) : (
                      filteredTopics.map((topic) => (
                        <button
                          key={topic.id}
                          type="button"
                          onClick={() => handleTopicSelect(topic.id)}
                          className={`w-full text-left px-3 py-2.5 text-sm font-medium transition-colors flex items-start gap-2 ${
                            activeTopic === topic.id
                              ? 'bg-[#0B4778]/10 text-[#0B4778]'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          <span className={`mt-0.5 flex-shrink-0 ${activeTopic === topic.id ? 'text-[#0B4778]' : 'text-gray-500'}`}>
                            {topic.icon}
                          </span>
                          <span className="flex-1 leading-tight flex items-center gap-1">
                            {topic.label}
                            {topic.recommended && <Star className="h-3 w-3 text-[#0B4778] fill-current" />}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 min-h-0 overflow-y-auto border-t border-gray-200">
              <div className="h-full flex">
                {/* Desktop Topic List */}
                <div className="hidden md:block w-64 border-r border-gray-200 bg-gray-50 overflow-y-auto flex-shrink-0">
                  <div className="p-2">
                    {filteredTopics.length === 0 ? (
                      <div className="text-sm text-gray-600 px-2 py-2">No topics found.</div>
                    ) : (
                      <div className="space-y-1">
                        {filteredTopics.map((topic) => (
                          <button
                            key={topic.id}
                            onClick={() => setActiveTopic(topic.id)}
                            className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-start gap-2 ${
                              activeTopic === topic.id
                                ? 'bg-[#0B4778]/10 text-[#0B4778]'
                                : 'text-gray-700 hover:bg-gray-100'
                            }`}
                          >
                            <span className={`mt-0.5 ${activeTopic === topic.id ? 'text-[#0B4778]' : 'text-gray-500'}`}>
                              {topic.icon}
                            </span>
                            <span className="flex-1 leading-tight">{topic.label}</span>
                            {topic.recommended && (
                              <Star className="h-4 w-4 text-[#0B4778] ml-auto flex-shrink-0 mt-0.5" fill="currentColor" />
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 p-4 md:p-6">
                  {activeTopicData ? (
                    <div>{activeTopicData.content}</div>
                  ) : (
                    <div className="text-sm text-gray-600">No topic selected.</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-gray-200 flex-shrink-0">
            <div className="flex justify-end">
              <button
                onClick={handleClose}
                className="w-full md:w-auto px-4 py-2 rounded-full text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 transition-colors flex items-center justify-center md:justify-start gap-2"
              >
                <X className="h-4 w-4" />
                <span>Close</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerHelpModal;
